import { useState } from 'react';
import { X, User, Mail, AlertCircle } from 'lucide-react';
import { logger } from '../../utils/logger';
import { isDemoMode } from '../../utils/demoUtils';

const EditProfileModal = ({ show, onClose, userProfile, user, onSave, players = [] }) => {
  const [name, setName] = useState(userProfile?.name || user?.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isDemo = isDemoMode();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Block saving in demo mode
    if (isDemo) {
      setError('Profile editing is disabled in demo mode');
      return;
    }

    // Validate name
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name cannot be empty');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Name must be less than 50 characters');
      return;
    }

    // Check for duplicate names (exclude current user)
    const duplicatePlayer = players.find(
      p => p.name.toLowerCase() === trimmedName.toLowerCase() && p.id !== userProfile?.id
    );
    if (duplicatePlayer) {
      setError(`Name "${trimmedName}" is already taken by another user`);
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave(trimmedName);
      onClose();
    } catch (err) {
      logger.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-lg font-bold text-zinc-900">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="block text-xs font-bold text-zinc-700 mb-2 uppercase tracking-wider">
              <Mail className="w-3 h-3 inline mr-1" />
              Email
            </label>
            <input
              type="email"
              value={userProfile?.email || user?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-zinc-200 rounded bg-zinc-50 text-zinc-500 text-sm"
            />
            <p className="text-xs text-zinc-500 mt-1">Email cannot be changed</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-xs font-bold text-zinc-700 mb-2 uppercase tracking-wider">
              <User className="w-3 h-3 inline mr-1" />
              Display Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your display name"
              disabled={isSubmitting || isDemo}
              className="w-full px-3 py-2 border border-zinc-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm disabled:bg-zinc-50 disabled:text-zinc-500"
              autoFocus={!isDemo}
            />
            <p className="text-xs text-zinc-500 mt-1">
              {isDemo
                ? 'Editing is disabled in demo mode'
                : 'This name will be shown in the leaderboard and logs'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-zinc-300 rounded text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDemo ? 'Close' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isDemo}
              className="flex-1 px-4 py-2 bg-zinc-900 text-white rounded text-sm font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDemo ? 'Editing is disabled in demo mode' : ''}
            >
              {isSubmitting ? 'Saving...' : isDemo ? 'Disabled in Demo' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
